// Offline-first storage. Everything the POS needs to run a full shift with no
// network lives here in IndexedDB:
//   - checks:  open tabs + paid-but-not-yet-synced orders (source of truth)
//   - kv:      settings + cached menu/storeConfig
//
// Checks are the durable record; the sync layer reads paid+unsynced checks
// and submits them to the backend, marking them synced on success.

import { openDB, type IDBPDatabase } from "idb";
import type { Check } from "../types/models";

const DB_NAME = "dineplatform_pos";
const DB_VERSION = 1;

const CHECKS = "checks";
const KV = "kv";

let dbp: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> {
  if (!dbp) {
    dbp = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(CHECKS)) {
          const store = db.createObjectStore(CHECKS, { keyPath: "id" });
          store.createIndex("status", "status");
          store.createIndex("updatedAt", "updatedAt");
        }
        if (!db.objectStoreNames.contains(KV)) {
          db.createObjectStore(KV);
        }
      },
    });
  }
  return dbp;
}

// ---- Checks --------------------------------------------------------------

export async function putCheck(check: Check): Promise<void> {
  const db = await getDb();
  await db.put(CHECKS, check);
}

export async function getCheck(id: string): Promise<Check | undefined> {
  const db = await getDb();
  return db.get(CHECKS, id);
}

export async function getAllChecks(): Promise<Check[]> {
  const db = await getDb();
  return db.getAll(CHECKS);
}

export async function deleteCheck(id: string): Promise<void> {
  const db = await getDb();
  await db.delete(CHECKS, id);
}

/** Paid checks that still need to reach the backend. */
export async function getUnsyncedPaidChecks(): Promise<Check[]> {
  const all = await getAllChecks();
  return all.filter((c) => c.status === "paid" && !c.synced);
}

// ---- Key/value (settings + caches) --------------------------------------

export async function kvGet<T>(key: string): Promise<T | undefined> {
  const db = await getDb();
  return db.get(KV, key);
}

/** Close this tab's connection so the database can be deleted (device reset).
 *  A later call reopens it, so nothing else has to know. */
export async function closeDb(): Promise<void> {
  const p = dbp;
  dbp = null as unknown as typeof dbp;
  try { (await p)?.close(); } catch { /* already gone */ }
}

export async function kvSet<T>(key: string, value: T): Promise<void> {
  const db = await getDb();
  await db.put(KV, value, key);
}
